import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import { not } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import MessageBox from 'garaje/components/ui/classic/message-box';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

interface SourceMapping {
  value: string;
  name: string;
}

/**
 * Maps plugin ID to the MAC address source value used in badge-log.
 * Includes both production and staging IDs since they differ per environment.
 */
const PLUGIN_ID_TO_SOURCE: Record<string, SourceMapping> = {
  // Jamf
  '961b9755-c5ad-457a-bf9e-7b1945780ee0': { value: 'jamf', name: 'Jamf' }, // prod
  'e6a215da-c6bb-425a-9125-ce921e34f71f': { value: 'jamf', name: 'Jamf' }, // staging
  // Cisco Meraki
  '71a29345-0d89-4d3d-b7fb-758efcf99bf9': { value: 'meraki', name: 'Cisco Meraki' }, // prod
  '9a8b891e-0a6c-4802-8414-d9cbdb41146d': { value: 'meraki', name: 'Cisco Meraki' }, // staging
  // Aruba Central
  'f930d4da-9477-4c6c-a2d9-5b2a53f728b1': { value: 'aruba_central', name: 'Aruba Central' }, // prod
  'da31e205-e3c3-48fb-b785-c7ed5e67f7eb': { value: 'aruba_central', name: 'Aruba Central' }, // staging
  // Microsoft Intune
  'ce5eb40d-be24-49ed-968f-270ec6c0ef8d': { value: 'intune', name: 'Microsoft Intune' }, // prod
  '19101c0e-5fb9-4168-99ec-44784b3f2340': { value: 'intune', name: 'Microsoft Intune' }, // staging
};

/** Plugin IDs that sync MAC addresses on install without a macSyncEnabled config step. */
const MAC_SYNC_ON_INSTALL: Set<string> = new Set([
  'ce5eb40d-be24-49ed-968f-270ec6c0ef8d', // Microsoft Intune prod
  '19101c0e-5fb9-4168-99ec-44784b3f2340', // Microsoft Intune staging
]);

function resolveSourceFromPlugin(pluginId: string): SourceMapping | undefined {
  return PLUGIN_ID_TO_SOURCE[pluginId];
}

interface PriorityOption {
  label: string;
  value: number;
}

const PRIORITY_OPTIONS: PriorityOption[] = [
  { label: '1 (Highest)', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5 (Lowest)', value: 5 },
];

interface SourcePriorityItem {
  source: string;
  name: string;
  priority: number;
}

interface PriorityApiEntry {
  source: string;
  priority: number;
}

interface PriorityApiResponse {
  companyId: number;
  enabled: boolean;
  priorities: PriorityApiEntry[];
}

interface SourcePrioritySettingsSignature {
  Args: {
    companyId: string;
    availableSources: SourcePriorityItem[];
    onClose: () => void;
    onSave: (config: PriorityApiResponse) => void;
  };
}

export default class SourcePrioritySettings extends Component<SourcePrioritySettingsSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;

  @tracked enabled = false;
  @tracked items: SourcePriorityItem[] = [];
  @tracked hasUnsavedChanges = false;
  @tracked isLoading = false;

  priorityOptions = PRIORITY_OPTIONS;

  constructor(owner: unknown, args: SourcePrioritySettingsSignature['Args']) {
    super(owner, args);
    void this.loadConfig.perform();
  }

  /**
   * Merge API priorities with available sources.
   * Sources from the API keep their priority. Available sources not in the
   * API response get priority 1 (default).
   */
  private buildItems(priorities: PriorityApiEntry[]): SourcePriorityItem[] {
    const available = this.args.availableSources;
    if (priorities.length === 0) {
      return available.map((s) => ({ ...s, priority: 1 }));
    }

    const apiMap = new Map(priorities.map((p) => [p.source, p.priority]));
    return available.map((s) => ({
      ...s,
      // eslint-disable-next-line ember/use-ember-get-and-set
      priority: apiMap.get(s.source) ?? 1,
    }));
  }

  selectedPriorityOption(item: SourcePriorityItem): PriorityOption {
    return PRIORITY_OPTIONS.find((o) => o.value === item.priority) ?? PRIORITY_OPTIONS[0]!;
  }

  loadConfig = dropTask(async () => {
    this.isLoading = true;
    try {
      // eslint-disable-next-line ember/use-ember-get-and-set
      const url = urlBuilder.macAddressSourcePriorities.get(this.args.companyId);
      const response: PriorityApiResponse = await this.ajaxFetch.request(url, { type: 'GET' });
      this.enabled = response.enabled;
      this.items = this.buildItems(response.priorities);
    } catch (e: unknown) {
      this.flashMessages.showAndHideFlash('error', 'Error loading source priority config', {
        details: parseErrorForDisplay(e),
      });
      this.items = this.buildItems([]);
    } finally {
      this.isLoading = false;
    }
  });

  saveConfig = dropTask(async () => {
    try {
      // eslint-disable-next-line ember/use-ember-get-and-set
      const url = urlBuilder.macAddressSourcePriorities.set();
      const priorities = this.items.map((item) => ({
        source: item.source,
        priority: item.priority,
      }));

      const response: PriorityApiResponse = await this.ajaxFetch.request(url, {
        type: 'PUT',
        contentType: 'application/json',
        data: JSON.stringify({
          companyId: parseInt(this.args.companyId, 10),
          enabled: this.enabled,
          priorities,
        }),
      });

      this.hasUnsavedChanges = false;
      this.flashMessages.showAndHideFlash('success', 'Source priority settings saved');
      this.args.onSave(response);
      this.args.onClose();
    } catch (e: unknown) {
      this.flashMessages.showAndHideFlash('error', 'Error saving source priority config', {
        details: parseErrorForDisplay(e),
      });
    }
  });

  @action
  toggleEnabled(checked: boolean): void {
    this.enabled = checked;
    this.hasUnsavedChanges = true;
  }

  @action
  changePriority(item: SourcePriorityItem, selected: PriorityOption): void {
    this.items = this.items.map((i) => (i.source === item.source ? { ...i, priority: selected.value } : i));
    this.hasUnsavedChanges = true;
  }

  @action
  save(): void {
    void this.saveConfig.perform();
  }

  <template>
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-test-source-priority-modal>
      <div class="bg-white rounded-[8px] shadow-xl w-[520px] max-h-[80vh] overflow-y-auto">
        {{! Header }}
        <div class="flex items-center justify-between px-6 py-4 border-b border-carbon-10">
          <h3 class="text-lg font-semibold text-carbon-70 mb-0">Source priority</h3>
          <button
            type="button"
            class="text-carbon-40 hover:text-carbon-60 text-xl leading-none"
            data-test-source-priority-close
            {{on "click" @onClose}}
          >
            &times;
          </button>
        </div>

        {{! Body }}
        <div class="px-6 py-4">
          {{#if this.isLoading}}
            <div class="flex justify-center py-8">
              <LoadingSpinnerPulse />
            </div>
          {{else}}
            {{! Toggle }}
            <div class="flex items-center justify-between mb-4 pb-4 border-b border-carbon-10">
              <div class="pr-4">
                <p class="font-semibold text-carbon-70 mb-1">Enable source priority</p>
                <p class="text-sm text-carbon-50 mb-0">
                  When enabled, lower-priority sources cannot overwrite mappings from higher-priority sources. Sources
                  at the same level can overwrite each other. Manual edits will always take priority over all other
                  sources and won't be overwritten by other sources.
                </p>
              </div>
              <SlideToggle
                @isChecked={{this.enabled}}
                @onClick={{this.toggleEnabled}}
                data-test-source-priority-toggle
              />
            </div>

            <MessageBox @type="info" data-test-source-priority-manual-info>
              <p class="mb-0">
                <strong>Manual</strong>
                — Highest priority when enabled. Cannot be overwritten by other sources.
              </p>
            </MessageBox>

            {{! Priority list }}
            <div class="mt-4 {{unless this.enabled 'opacity-50 pointer-events-none'}}">
              <p class="text-sm text-carbon-50 mb-3">
                Assign a priority level to each source. Sources with the same level can overwrite each other. Lower
                numbers = higher priority.
              </p>

              <div class="space-y-2" data-test-source-priority-list>
                {{#each this.items as |item|}}
                  <div
                    class="flex items-center justify-between px-3 py-2.5 bg-white border border-carbon-10 rounded-[6px]"
                    data-test-source-priority-item={{item.source}}
                  >
                    <span class="font-medium text-carbon-70">{{item.name}}</span>
                    <div class="w-[140px]">
                      <PowerSelect
                        class="!mb-0"
                        @selected={{this.selectedPriorityOption item}}
                        @options={{this.priorityOptions}}
                        @searchEnabled={{false}}
                        @onChange={{fn this.changePriority item}}
                        data-test-source-priority-select={{item.source}}
                        as |option|
                      >
                        {{option.label}}
                      </PowerSelect>
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>
          {{/if}}
        </div>

        {{! Footer }}
        {{#unless this.isLoading}}
          <div class="flex justify-end gap-2 px-6 py-4 border-t border-carbon-10">
            <button
              type="button"
              class="btn btn--small btn--outline"
              data-test-source-priority-cancel
              {{on "click" @onClose}}
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn--small btn--brand"
              disabled={{not this.hasUnsavedChanges}}
              data-test-source-priority-save
              {{on "click" this.save}}
            >
              {{if this.saveConfig.isRunning "Saving..." "Save"}}
            </button>
          </div>
        {{/unless}}
      </div>
    </div>
  </template>
}

export { MAC_SYNC_ON_INSTALL, PLUGIN_ID_TO_SOURCE, resolveSourceFromPlugin };
export type { PriorityApiResponse, SourcePriorityItem };
